// Account preferences (P15): stored locally (small, non-sensitive) for instant/offline use and synced to the
// server per user so they follow the user to other devices. The user's latest change wins.
import type { PrefStore } from '@/modules/panes/ports';
import { api } from '@/api/client';

const K = 'taskpanes-v3';
export interface ServerPrefs {
  theme?: string; density?: string; accent?: string; lang?: string; calPrimary?: string; calAlt?: string; mobileLayout?: string; timezone?: string;
  notifications?: { email?: boolean; deadline?: boolean; share?: boolean; assign?: boolean };
  mentionsReadAt?: number;
  /** v7: Notifications (comments) read marker and the desktop screen saver switch */
  notificationsReadAt?: number; screenSaver?: boolean;
}

function readLocal(local: PrefStore): ServerPrefs {
  let p: Record<string, unknown> = {};
  try { p = JSON.parse(local.get(K + '-prefs') || '{}'); } catch { /* ignore */ }
  const out: ServerPrefs = { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  const theme = local.get(K + '-theme'), density = local.get(K + '-density'), accent = local.get(K + '-accent'), ml = local.get(K + '-mlayout');
  if (theme) out.theme = theme; if (density) out.density = density; if (accent) out.accent = accent; if (ml) out.mobileLayout = ml;
  if (p.lang) out.lang = String(p.lang); if (p.calPrimary) out.calPrimary = String(p.calPrimary); if (p.calAlt) out.calAlt = String(p.calAlt);
  const mread = Number(local.get(K + '-mread') || 0);
  if (mread > 0) out.mentionsReadAt = Math.floor(mread);
  const nread = Number(local.get(K + '-nread') || 0);
  if (nread > 0) out.notificationsReadAt = Math.floor(nread);
  if (typeof p.saver === 'boolean') out.screenSaver = p.saver;
  out.notifications = { email: (p.nEmail as boolean | undefined) ?? true, deadline: (p.nDl as boolean | undefined) ?? true, share: (p.nShare as boolean | undefined) ?? false, assign: (p.nAssign as boolean | undefined) ?? true };
  return out;
}

export class PrefsSync implements PrefStore {
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private readonly local: PrefStore) {}
  get(key: string): string | null { return this.local.get(key); }
  set(key: string, value: string): void {
    this.local.set(key, value);
    if (/-(theme|density|accent|mlayout|prefs|mread|nread)$/.test(key)) { this.local.set(K + '-prefs-dirty', '1'); this.schedule(); }
  }
  private schedule(): void { if (this.timer) clearTimeout(this.timer); this.timer = setTimeout(() => void this.push(), 800); }

  async push(): Promise<void> {
    if (!this.local.get(K + '-prefs-dirty')) return;
    let rev = Number(this.local.get(K + '-prefs-rev') || 0);
    const prefs = readLocal(this.local);
    try {
      for (let i = 0; i < 2; i++) {
        try { const r = await api<{ revision: number }>('PUT', '/me/preferences', { prefs, base_revision: rev }); rev = r.revision; break; }
        catch (e) { if ((e as { status?: number }).status !== 409) throw e; rev = (await api<{ revision: number }>('GET', '/me/preferences')).revision; }
      }
      this.local.set(K + '-prefs-rev', String(rev));
      this.local.set(K + '-prefs-dirty', '');
    } catch (e) {
      // offline: stays dirty. Rejected (422, a value this server does not accept): retrying cannot succeed, and while
      // dirty this device never adopts preferences from other devices — keep the values locally, stop retrying.
      if ((e as { status?: number }).status === 422) this.local.set(K + '-prefs-dirty', '');
    }
  }

  /** Returns server preferences to apply when they are newer than this device's copy. */
  async pull(): Promise<ServerPrefs | null> {
    if (this.local.get(K + '-prefs-dirty')) { await this.push(); return null; }
    let r: { prefs: ServerPrefs; revision: number };
    try { r = await api('GET', '/me/preferences'); } catch { return null; }
    if (r.revision <= Number(this.local.get(K + '-prefs-rev') || 0)) return null;
    const p = r.prefs;
    if (p.theme) this.local.set(K + '-theme', p.theme);
    if (p.density) this.local.set(K + '-density', p.density);
    if (p.accent) this.local.set(K + '-accent', p.accent);
    if (p.mobileLayout) this.local.set(K + '-mlayout', p.mobileLayout);
    if (p.mentionsReadAt && p.mentionsReadAt > Number(this.local.get(K + '-mread') || 0)) this.local.set(K + '-mread', String(p.mentionsReadAt));
    if (p.notificationsReadAt && p.notificationsReadAt > Number(this.local.get(K + '-nread') || 0)) this.local.set(K + '-nread', String(p.notificationsReadAt));
    let cur: Record<string, unknown> = {}; try { cur = JSON.parse(this.local.get(K + '-prefs') || '{}'); } catch { /* ignore */ }
    const merged = { ...cur, ...(p.lang ? { lang: p.lang } : {}), ...(p.calPrimary ? { calPrimary: p.calPrimary } : {}), ...(p.calAlt ? { calAlt: p.calAlt } : {}),
      ...(p.notifications ? { nEmail: p.notifications.email, nDl: p.notifications.deadline, nShare: p.notifications.share, ...(typeof p.notifications.assign === 'boolean' ? { nAssign: p.notifications.assign } : {}) } : {}), ...(typeof p.screenSaver === 'boolean' ? { saver: p.screenSaver } : {}) };
    this.local.set(K + '-prefs', JSON.stringify(merged));
    this.local.set(K + '-prefs-rev', String(r.revision));
    return p;
  }
}
